import { Component } from 'react'
import { motion } from 'motion/react'

/**
 * Componente de fallback para ErrorBoundary
 * Se muestra cuando ocurre un error en la aplicación
 *
 * @component
 * @param {Object} props
 * @param {Error} props.error - El error capturado
 * @param {Function} props.resetError - Función para resetear el error y reintentar
 * @returns {JSX.Element}
 */
export function ErrorFallback({ error, resetError }) {
  // Función para solicitar permisos de cámara
  const handleCameraPermission = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true })
      resetError() // Intentar de nuevo después de obtener permisos
    } catch (err) {
      console.error('Error al solicitar permisos de cámara:', err)
      alert(
        'No se pudieron obtener los permisos de cámara. Por favor, verifica la configuración de tu navegador.'
      )
    }
  }

  // Función para volver a la página anterior
  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      window.location.href = '/'
    }
  }

  // Configuración de animaciones escalonadas
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12,
      },
    },
  }

  const logoVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 15,
        duration: 0.6,
      },
    },
  }

  return (
    <section
      className='glass-bg flex min-h-screen flex-col items-center justify-center p-5 text-center'
      role='alert'
      aria-live='assertive'
      aria-atomic='true'
    >
      <motion.article
        className='glass-card w-full max-w-md rounded-2xl p-8 shadow-2xl'
        variants={containerVariants}
        initial='hidden'
        animate='visible'
      >
        {/* Logo/Icono de error animado */}
        <motion.div
          className='mb-4 text-6xl'
          variants={logoVariants}
          role='img'
          aria-label='Icono de error'
        >
          😕
        </motion.div>

        {/* Título animado */}
        <motion.h1
          className='mb-2 text-2xl font-bold text-gray-800'
          variants={itemVariants}
          id='error-title'
        >
          Algo salió mal
        </motion.h1>

        {/* Descripción animada */}
        <motion.p className='mb-6 text-gray-600' variants={itemVariants} id='error-description'>
          Lo sentimos, ha ocurrido un error inesperado. El equipo de desarrollo ha sido notificado
          automáticamente.
        </motion.p>

        {/* Detalles del error (colapsable) animado */}
        <motion.details className='mb-6 text-left' variants={itemVariants}>
          <summary className='mb-2 cursor-pointer rounded font-medium text-blue-600 hover:text-blue-800 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none'>
            Ver detalles técnicos del error
          </summary>
          <pre
            className='mt-2 max-h-40 overflow-auto rounded border border-gray-300 bg-gray-100 p-4 text-xs text-gray-800'
            role='region'
            aria-label='Detalles técnicos del error'
            tabIndex={0}
          >
            {error.toString()}
            {error.stack && (
              <>
                {'\n\n'}
                {error.stack}
              </>
            )}
          </pre>
        </motion.details>

        {/* Botones de acción animados */}
        <motion.div className='flex flex-col justify-center gap-3' variants={itemVariants}>
          <motion.button
            onClick={resetError}
            className='rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white shadow-md transition-colors duration-200 hover:bg-blue-700 hover:shadow-lg focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none active:bg-blue-800'
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label='Intentar de nuevo'
            type='button'
          >
            Intentar de nuevo
          </motion.button>

          <motion.button
            onClick={handleCameraPermission}
            className='rounded-lg bg-green-600 px-6 py-3 font-semibold text-white shadow-md transition-colors duration-200 hover:bg-green-700 hover:shadow-lg focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none active:bg-green-800'
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label='Activar permisos de cámara'
            type='button'
          >
            📷 Activar cámara
          </motion.button>

          <motion.button
            onClick={handleGoBack}
            className='rounded-lg bg-gray-200 px-6 py-3 font-semibold text-gray-800 transition-colors duration-200 hover:bg-gray-300 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:outline-none active:bg-gray-400'
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label='Volver a la página anterior'
            type='button'
          >
            ← Volver atrás
          </motion.button>

          <motion.button
            onClick={() => (window.location.href = '/')}
            className='rounded-lg bg-gray-200 px-6 py-3 font-semibold text-gray-800 transition-colors duration-200 hover:bg-gray-300 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:outline-none active:bg-gray-400'
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label='Volver a la página de inicio'
            type='button'
          >
            🏠 Volver al inicio
          </motion.button>
        </motion.div>

        {/* Información adicional animada (último elemento con delay) */}
        <motion.p
          className='mt-6 text-xs text-gray-500'
          variants={itemVariants}
          aria-label={`Identificador de error: ${Date.now().toString(36)}`}
        >
          Error ID: {Date.now().toString(36)}
        </motion.p>
      </motion.article>
    </section>
  )
}

/**
 * ErrorBoundary is a React component that catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 *
 * @class ErrorBoundary
 * @extends React.Component
 *
 * @prop {React.ReactNode} children - The child components to render.
 * @prop {React.ReactNode|function} [fallback] - A fallback UI to display when an error is caught.
 *        Can be a React node or a function that receives an object with `error` and `resetErrorBoundary`.
 *
 * @state {boolean} hasError - Indicates if an error has been caught.
 * @state {Error|null} error - The error object that was caught.
 *
 * @method static getDerivedStateFromError(error) - Updates state when an error is caught.
 * @method resetErrorBoundary - Resets the error boundary state to allow retrying rendering.
 *
 * @example
 * <ErrorBoundary fallback={<div>Something went wrong.</div>}>
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ? (
        typeof this.props.fallback === 'function' ? (
          this.props.fallback({
            error: this.state.error,
            resetErrorBoundary: this.resetErrorBoundary,
          })
        ) : (
          this.props.fallback
        )
      ) : (
        <ErrorFallback error={this.state.error} resetError={this.resetErrorBoundary} />
      )
    }
    return this.props.children
  }
}
